/** GMAPS_SDK_KEY_SETUP control — enable the Maps SDK + restrict the auto-created Android key.
 *
 *  Fetches the live Maps status (gmaps_status). The step is GATED on the Firebase Android
 *  app existing (detail.gate_reason present → do that step first). Otherwise shows the
 *  current key + its package/SHA-1 restriction, and lets ops set the SHA-1 and run
 *  save_gmaps_key (enables the SDK, restricts the key, reads its value server-side).
 */
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Alert, Box, Button, Chip, CircularProgress, Divider, Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import { items } from '@/api/preflight';
import type { GmapsDetail, PreflightItem } from '@/api/preflight';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { copyOnlyActions } from '../tenantLinks';

function errMessage(e: unknown, fallback: string): string {
  return e instanceof ConsoleApiError ? e.message : fallback;
}

/** Human-readable text for a failed op — the server's error `detail` (a string on the
 *  reason=error path) wins, else the short reason code, else a generic fallback. */
function opErrorText(res: { reason?: string | null; detail?: unknown }): string {
  return (typeof res.detail === 'string' && res.detail) || res.reason || 'Operation failed.';
}

/** True hard-error text from the live status probe (ok=false with a Google detail), if any. */
function statusErrorText(s: { ok: boolean; reason?: string | null; detail?: unknown } | undefined): string | null {
  if (s && s.ok === false && s.reason === 'error' && typeof s.detail === 'string') return s.detail;
  return null;
}

export function GmapsSdkKeySetupControl({
  item,
  topId,
  canManage,
  onChanged,
}: {
  item: PreflightItem;
  topId: number;
  canManage: boolean;
  onChanged: () => void;
}) {
  const snackbar = useSnackbar();
  const seed = item.detail as unknown as GmapsDetail;

  const query = useQuery({
    queryKey: ['preflight', 'gmapsStatus', topId, item.id],
    queryFn: () => items.fetchGmapsStatus(topId, item.id),
    enabled: canManage,
  });
  const status = query.data;
  const detail = status?.detail ?? seed;

  // SHA-1 edit overlay → falls back to the server debug SHA-1.
  const [sha1Edit, setSha1Edit] = useState<string | null>(null);
  const sha1 = sha1Edit ?? detail?.debug_sha1 ?? '';
  // Persistent, dismissible banner for the most recent operation failure (in addition to
  // the transient snackbar) so ops can read/act on the actual Google error.
  const [opError, setOpError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => items.saveGmapsKey(topId, item.id, { sha1: sha1.trim() || undefined }),
    onMutate: () => setOpError(null),
    onSuccess: (res) => {
      if (res.ok) {
        snackbar.show('Maps key configured.', 'success');
        setSha1Edit(null);
        setOpError(null);
        void query.refetch();
        onChanged();
      } else {
        setOpError(opErrorText(res));
        const msg =
          res.reason === 'no_android_app'
            ? 'Create the Firebase Android app first.'
            : res.reason === 'no_key'
              ? 'No auto-created Maps key was found for the project.'
              : 'Could not configure the Maps key.';
        snackbar.show(msg, 'error');
      }
    },
    onError: (e) => {
      setOpError(e instanceof ConsoleApiError ? e.message : 'Operation failed.');
      snackbar.show(errMessage(e, 'Could not configure the Maps key.'), 'error');
    },
  });

  // ---- gate: automation not configured on the server
  if (!detail?.configured) {
    return (
      <Box sx={{ mb: 1.5 }}>
        <Alert severity="info">
          Google Maps automation isn&apos;t configured on the server (no Google service account) — this step
          must be completed manually in the Google Cloud console.
        </Alert>
      </Box>
    );
  }

  // ---- gate: blocked (e.g. no Firebase Android app yet)
  if (detail.gate_reason) {
    return (
      <Box sx={{ mb: 1.5 }}>
        <Stack spacing={1}>
          <Chip size="small" color="info" variant="outlined" label={detail.environment || 'unknown env'} sx={{ alignSelf: 'flex-start' }} />
          <Alert severity="info">
            This step is blocked: {detail.gate_reason}. Complete the Firebase Android app (FIREBASE_PROJECT_SETUP)
            first — the Maps key is restricted to that app&apos;s package.
          </Alert>
        </Stack>
      </Box>
    );
  }

  const keySet = detail.completion?.maps_key_set === true;
  // Persistent banner: a fresh operation error wins; else surface a hard status probe error.
  const bannerError = opError ?? statusErrorText(status);

  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip size="small" color="info" variant="outlined" label={detail.environment || 'unknown env'} />
          <Typography variant="caption" color="text.secondary">
            Configuring the <strong>{detail.environment}</strong> Maps SDK key.
          </Typography>
        </Stack>

        {bannerError && (
          <Alert severity="error" onClose={() => setOpError(null)} sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {bannerError}
          </Alert>
        )}

        {query.isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              Loading the live Maps status…
            </Typography>
          </Box>
        )}

        {!canManage && (
          <Typography variant="caption" color="text.secondary">
            Manage permission is required to load + configure the Maps key.
          </Typography>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          <Chip
            size="small"
            variant="outlined"
            color={keySet ? 'success' : 'default'}
            icon={keySet ? <CheckCircleIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
            label="Maps key"
          />
        </Box>

        {/* current restriction (read-only) */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 1.5, rowGap: 0.25 }}>
          <Typography variant="caption" color="text.secondary">
            Current key
          </Typography>
          <Typography variant="caption" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
            {/* A masked key yields no copy — copyOnlyActions drops it. */}
            <ValueActions actions={copyOnlyActions(detail.maps_key, 'Copy maps key')}>
              {detail.maps_key || '— not set'}
            </ValueActions>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Restricted to package
          </Typography>
          <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
            <ValueActions actions={copyOnlyActions(detail.package_name, 'Copy package name')}>
              {detail.package_name || '—'}
            </ValueActions>
          </Typography>
        </Box>

        {canManage && (
          <>
            <Divider textAlign="left">
              <Typography variant="caption" color="text.secondary">
                Android key restriction
              </Typography>
            </Divider>
            <TextField
              size="small"
              fullWidth
              label="SHA-1 fingerprint"
              value={sha1}
              disabled={save.isPending}
              onChange={(e) => setSha1Edit(e.target.value)}
              helperText="Debug cert for initial setup — replaced by the Play production signing SHA in the follow-up step."
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Button size="small" variant="contained" disabled={save.isPending} onClick={() => save.mutate()}>
                Configure Maps key
              </Button>
              <Button size="small" disabled={query.isFetching || save.isPending} onClick={() => void query.refetch()}>
                Refresh
              </Button>
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
}
